/**
 * The signed-in user's own account (`/profile`) — reachable by every role, with
 * no left-nav item; the top-bar profile menu links here.
 *
 * Three things a user can do to themselves, each its own card so a failure in
 * one never blocks another: change their picture, change their name, change
 * their password. A fourth card is read-only — the session facts (tenant,
 * partner, auth method, elevation) that are the first thing anyone needs when
 * working out why a screen is refusing them.
 *
 * Light/dark is not one of them — it is resolved entirely from the applied
 * brand's "light/dark default" (`Settings -> Branding`), never chosen here.
 *
 * The identity comes from `GET /v1/me` rather than from the session principal:
 * the principal is a snapshot resolved at sign-in, and this screen has to show
 * what is true now, including `created_at`, which the principal does not carry.
 * After any write that the shell also renders (name, avatar) the session is
 * refreshed so the top bar agrees with this screen immediately.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { ApiError } from '../api/errors';
import type { AuthMethod, UserRole } from '../api/schema';
import type { Profile } from '../api/types';
import { roleLabel } from '../auth/roles';
import { useSession } from '../auth/session';
import { useAvatar } from '../auth/useAvatar';
import { Button, ErrorState, FormField, Input, PasswordInput, Skeleton, useToast } from '../components';
import { initialsFor } from '../lib/initials';
import { useLoadState } from '../lib/useLoadState';

import { fmtDate } from './admin/format';
import './screens.css';

/** Mirrors `app.services.profile.AVATAR_CONTENT_TYPES` — the server re-checks. */
const AVATAR_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;

/** Mirrors `app.services.profile.MAX_AVATAR_BYTES`. */
const MAX_AVATAR_BYTES = 2 * 1024 * 1024;

/** Mirrors `app.core.auth.passwords.MIN_PASSWORD_LENGTH`. */
const MIN_PASSWORD_LENGTH = 8;

const AUTH_METHOD_LABELS: Record<AuthMethod, string> = {
  entra_oidc: 'Microsoft Entra ID',
  dev_jwt: 'Dev JWT',
  api_key: 'API key',
  internal: 'Internal',
};

function authMethodLabel(method: AuthMethod | null | undefined): string {
  return method == null ? '—' : AUTH_METHOD_LABELS[method];
}

/** The message a failed write should show, preferring the server's own words. */
function problemText(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.message.trim().length > 0) {
    // A 500 carries a status line rather than an explanation; never show that.
    if (error.status < 500) return error.message;
  }
  return fallback;
}

// --------------------------------------------------------------- avatar card ---

interface AvatarCardProps {
  displayName: string | null;
  role: UserRole | null;
  hasAvatar: boolean;
  onChanged: (profile: Profile) => void;
}

function AvatarCard({ displayName, role, hasAvatar, onChanged }: AvatarCardProps): React.JSX.Element {
  const { api, refresh } = useSession();
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped on every successful write so a replacement picture is re-fetched even
  // when neither the flag nor the user id changed.
  const [version, setVersion] = useState(0);

  // Driven by this screen's own `has_avatar`, not the session's: the principal
  // is only re-resolved on `refresh()`, and the new picture should appear the
  // moment the upload returns.
  const { url } = useAvatar({ hasAvatar, version });
  const initials = initialsFor(displayName);

  const onPick = async (file: File | undefined): Promise<void> => {
    // Reset the control first: picking the same file twice in a row fires no
    // change event otherwise, so a retry after an error would look dead.
    if (inputRef.current !== null) inputRef.current.value = '';
    if (file === undefined) return;

    setError(null);
    if (!AVATAR_TYPES.includes(file.type as (typeof AVATAR_TYPES)[number])) {
      setError('That file type is not supported. Use a PNG, JPEG, WebP or GIF image.');
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setError('That image is too large. The limit is 2 MB.');
      return;
    }

    setBusy(true);
    try {
      onChanged(await api.me.avatar.upload(file));
      setVersion((v) => v + 1);
      await refresh();
      toast('Picture updated');
    } catch (err) {
      setError(problemText(err, 'Could not upload that picture. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async (): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      onChanged(await api.me.avatar.remove());
      setVersion((v) => v + 1);
      await refresh();
      toast('Picture removed');
    } catch (err) {
      setError(problemText(err, 'Could not remove that picture. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="profile-panel profile-panel--identity" aria-label="Profile picture">
      {url !== null ? (
        <img className="avatar profile-avatar profile-avatar--image" src={url} alt="Your avatar" />
      ) : (
        <span
          className={initials === null ? 'avatar profile-avatar avatar-empty' : 'avatar profile-avatar'}
          aria-hidden="true"
        >
          {initials ?? '?'}
        </span>
      )}

      <div className="profile-identity-text">
        <p className="profile-identity-name">{displayName ?? 'Unknown user'}</p>
        {role !== null ? <p className="profile-identity-role">{roleLabel(role)}</p> : null}

        <div className="profile-avatar-actions">
          <label className="ds-btn ds-btn--secondary ds-btn--sm profile-upload" htmlFor={inputId}>
            {hasAvatar ? 'Change picture' : 'Upload picture'}
          </label>
          <input
            ref={inputRef}
            id={inputId}
            type="file"
            className="sr-only"
            accept={AVATAR_TYPES.join(',')}
            aria-label="Upload a profile picture"
            disabled={busy}
            onChange={(e) => {
              void onPick(e.target.files?.[0]);
            }}
          />
          {hasAvatar ? (
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => {
                void onRemove();
              }}
            >
              Remove picture
            </Button>
          ) : null}
        </div>
        <p className="profile-hint">PNG, JPEG, WebP or GIF, up to 2 MB.</p>
        {error !== null ? (
          <p className="profile-note profile-note--error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </section>
  );
}

// ----------------------------------------------------------------- details ---

function DetailsCard({
  profile,
  onChanged,
}: {
  profile: Profile;
  onChanged: (profile: Profile) => void;
}): React.JSX.Element {
  const { api, refresh } = useSession();
  const { toast } = useToast();
  const [name, setName] = useState(profile.display_name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Adopt a name that changed underneath us (an upload response, a refetch).
  useEffect(() => {
    setName(profile.display_name);
  }, [profile.display_name]);

  const trimmed = name.trim();
  const dirty = trimmed !== profile.display_name;
  const canSave = dirty && trimmed.length > 0 && !busy;

  const onSave = async (): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      onChanged(await api.me.update({ display_name: trimmed }));
      await refresh();
      toast('Name updated');
    } catch (err) {
      setError(problemText(err, 'Could not save your name. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="profile-panel" aria-label="Your details">
      <h2 className="profile-panel-title">Your details</h2>

      <FormField label="Display name" hint="How your name appears across Docsight.">
        {(p) => (
          <Input
            {...p}
            value={name}
            maxLength={200}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        )}
      </FormField>

      <dl className="screen-meta profile-meta">
        <dt>Email</dt>
        <dd className="mono">{profile.email}</dd>
        <dt>Role</dt>
        <dd>{roleLabel(profile.role)}</dd>
        <dt>Member since</dt>
        <dd>{fmtDate(profile.created_at)}</dd>
      </dl>

      <p className="profile-hint">
        Your email and role are set by an administrator — ask yours if either needs to change.
      </p>

      {error !== null ? (
        <p className="profile-note profile-note--error" role="alert">
          {error}
        </p>
      ) : null}

      <Button
        className="profile-submit"
        disabled={!canSave}
        loading={busy}
        loadingLabel="Saving…"
        onClick={() => {
          void onSave();
        }}
      >
        Save name
      </Button>
    </section>
  );
}

// ---------------------------------------------------------------- password ---

function PasswordCard(): React.JSX.Element {
  const { api } = useSession();
  const { toast } = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentError, setCurrentError] = useState<string | null>(null);

  // Checked here as well as on the server so the button is honestly disabled
  // rather than inviting a round-trip that is already known to fail.
  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirm.length > 0 && next !== confirm;
  const complete = current.length > 0 && next.length > 0 && confirm.length > 0;
  const canSubmit = complete && !tooShort && !mismatch && !busy;

  const onSubmit = async (): Promise<void> => {
    setError(null);
    setCurrentError(null);
    setBusy(true);
    try {
      await api.me.changePassword({ current_password: current, new_password: next });
      toast('Password changed');
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        // Point at the field that actually failed and keep the new password, so
        // the only thing to retype is the one that was wrong.
        setCurrentError('The current password is incorrect');
        setCurrent('');
      } else {
        setError(problemText(err, 'Could not change your password. Please try again.'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="profile-panel" aria-label="Password">
      <h2 className="profile-panel-title">Password</h2>
      <p className="profile-hint">
        Changing your password needs your current one, so a signed-in browser left unattended cannot
        be used to lock you out.
      </p>

      <FormField label="Current password" error={currentError ?? undefined}>
        {(p) => (
          <PasswordInput
            {...p}
            className="ds-input"
            value={current}
            autoComplete="current-password"
            onChange={(e) => {
              setCurrent(e.target.value);
              setCurrentError(null);
            }}
          />
        )}
      </FormField>

      <FormField
        label="New password"
        hint={`At least ${String(MIN_PASSWORD_LENGTH)} characters.`}
        error={tooShort ? `Use at least ${String(MIN_PASSWORD_LENGTH)} characters` : undefined}
      >
        {(p) => (
          <PasswordInput
            {...p}
            className="ds-input"
            value={next}
            autoComplete="new-password"
            onChange={(e) => {
              setNext(e.target.value);
            }}
          />
        )}
      </FormField>

      <FormField
        label="Confirm new password"
        error={mismatch ? 'Those passwords do not match' : undefined}
      >
        {(p) => (
          <PasswordInput
            {...p}
            className="ds-input"
            value={confirm}
            autoComplete="new-password"
            onChange={(e) => {
              setConfirm(e.target.value);
            }}
          />
        )}
      </FormField>

      {error !== null ? (
        <p className="profile-note profile-note--error" role="alert">
          {error}
        </p>
      ) : null}

      <Button
        className="profile-submit"
        disabled={!canSubmit}
        loading={busy}
        loadingLabel="Changing…"
        onClick={() => {
          void onSubmit();
        }}
      >
        Change password
      </Button>
    </section>
  );
}

// ----------------------------------------------------------------- session ---

function SessionCard({ profile }: { profile: Profile | null }): React.JSX.Element {
  const { principal } = useSession();

  return (
    <section className="profile-panel" aria-label="Session">
      <h2 className="profile-panel-title">Session</h2>
      <p className="profile-hint">
        What this browser session is authenticated as. Useful when a screen refuses you and you need
        to say why.
      </p>
      <dl className="screen-meta profile-meta">
        <dt>User ID</dt>
        <dd className="mono">{profile?.user_id ?? principal?.user_id ?? '—'}</dd>
        <dt>Tenant</dt>
        <dd className="mono">{principal?.tenant_id ?? 'No tenant (platform role)'}</dd>
        <dt>Partner</dt>
        <dd className="mono">{principal?.partner_id ?? '—'}</dd>
        <dt>Signed in with</dt>
        <dd>{authMethodLabel(principal?.auth_method)}</dd>
        <dt>Access</dt>
        <dd>{principal?.elevated === true ? 'Elevated' : 'Normal'}</dd>
        <dt>Permissions</dt>
        <dd>{principal?.permissions.length ?? 0} granted</dd>
      </dl>
    </section>
  );
}

// ------------------------------------------------------------------ screen ---

export function ProfileScreen(): React.JSX.Element {
  const { principal, api } = useSession();
  const fetchProfile = useCallback((signal: AbortSignal): Promise<Profile> => api.me.get(signal), [api]);
  const load = useLoadState(fetchProfile);

  // A write (avatar, name) lands here for instant feedback with no refetch;
  // `retry()` clears it so a fresh load's truth always wins over a stale patch.
  const [override, setOverride] = useState<Profile | null>(null);
  const reload = load.status === 'ready' || load.status === 'error' ? load.reload : undefined;
  const retry = useCallback(() => {
    setOverride(null);
    reload?.();
  }, [reload]);

  const profile = override ?? (load.status === 'ready' ? load.data : null);
  const onChanged = (next: Profile): void => {
    setOverride(next);
  };

  // The principal is already resolved, so name and role render immediately —
  // the fetch only fills in the parts it does not carry.
  const displayName = profile?.display_name ?? principal?.display_name ?? null;
  const role = profile?.role ?? principal?.role ?? null;

  return (
    <section className="screen profile-screen" aria-label="Profile">
      <AvatarCard
        displayName={displayName}
        role={role}
        hasAvatar={profile?.has_avatar ?? false}
        onChanged={onChanged}
      />

      {load.status === 'loading' ? <Skeleton height={220} /> : null}

      {load.status === 'error' ? (
        <ErrorState
          title="Couldn't load your profile"
          body="Your account details failed to load."
          actions={
            <Button variant="secondary" onClick={retry}>
              Retry
            </Button>
          }
        />
      ) : null}

      {profile !== null ? <DetailsCard profile={profile} onChanged={onChanged} /> : null}
      {profile !== null ? <PasswordCard /> : null}

      <SessionCard profile={profile} />
    </section>
  );
}
